import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  fetchOrdersByStatus,
  fetchRevenueSummary,
  fetchSuccessRate,
  fetchTopDeliverers
} from '../api/analytics';
import { ORDER_STATUS_LABELS, StatusBadge } from '../components/StatusBadge';
import { OrderStatus } from '../api/orders';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';

// Palette par statut (cohérente avec StatusBadge)
const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: '#94a3b8', // slate-400
  accepted: '#3b82f6', // blue-500
  picked_up: '#6366f1', // indigo-500
  in_transit: '#f59e0b', // amber-500
  delivered: '#10b981', // emerald-500
  cancelled: '#cbd5e1', // slate-300
  failed: '#ef4444' // red-500
};

const OUTCOME_COLORS = {
  delivered: '#10b981',
  cancelled: '#cbd5e1',
  failed: '#ef4444'
};

const StatCard = ({
  title,
  children,
  accent = 'brand'
}: {
  title: string;
  children: ReactNode;
  accent?: 'brand' | 'slate' | 'emerald';
}) => {
  const bar =
    accent === 'emerald'
      ? 'from-emerald-500 to-teal-500'
      : accent === 'slate'
        ? 'from-slate-600 to-slate-800'
        : 'from-brand-500 to-brand-700';

  return (
    <section className="card relative overflow-hidden p-6">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${bar}`} />
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
};

const Metric = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div>
    <div className="text-xs font-medium text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-semibold tracking-tight tabular-nums text-slate-900">{value}</div>
    {hint && <div className="mt-2 text-xs leading-relaxed text-slate-500">{hint}</div>}
  </div>
);

export const AnalyticsPage = () => {
  const byStatus = useQuery({ queryKey: ['analytics', 'byStatus'], queryFn: fetchOrdersByStatus });
  const revenue = useQuery({ queryKey: ['analytics', 'revenue'], queryFn: fetchRevenueSummary });
  const success = useQuery({ queryKey: ['analytics', 'successRate'], queryFn: fetchSuccessRate });
  const top = useQuery({ queryKey: ['analytics', 'topDeliverers'], queryFn: () => fetchTopDeliverers(10) });

  const loadingBlock = (
    <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
      <Spinner className="h-5 w-5 border-2 border-slate-300 border-t-brand-500" />
      Chargement…
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Analytics"
        description="Vue consolidée des revenus, du taux de succès et des performances livreurs. Données issues de l’API d’administration."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Revenus (livrés + payés)" accent="brand">
          {revenue.isLoading ? (
            loadingBlock
          ) : revenue.error ? (
            <p className="text-sm text-red-600">Impossible de charger les revenus.</p>
          ) : revenue.data ? (
            <Metric
              label="Brut"
              value={`${revenue.data.data.totalGross.toFixed(2)} ${revenue.data.data.currency}`}
              hint={`${revenue.data.data.totalOrdersCompleted} commande(s) terminées`}
            />
          ) : null}
        </StatCard>

        <StatCard title="Commission plateforme" accent="slate">
          {revenue.isLoading ? (
            loadingBlock
          ) : revenue.data ? (
            <Metric
              label="Commission"
              value={`${revenue.data.data.totalPlatformCommission.toFixed(2)} ${revenue.data.data.currency}`}
            />
          ) : null}
        </StatCard>

        <StatCard title="Revenu livreurs" accent="slate">
          {revenue.isLoading ? (
            loadingBlock
          ) : revenue.data ? (
            <Metric
              label="Total versé aux livreurs"
              value={`${revenue.data.data.totalDelivererRevenue.toFixed(2)} ${revenue.data.data.currency}`}
            />
          ) : null}
        </StatCard>

        <StatCard title="Taux de succès" accent="emerald">
          {success.isLoading ? (
            loadingBlock
          ) : success.data ? (
            <Metric
              label="Livraisons réussies"
              value={`${(success.data.data.successRate * 100).toFixed(1)} %`}
              hint={`${success.data.data.delivered} livrées · ${success.data.data.failed} échouées · ${success.data.data.cancelled} annulées`}
            />
          ) : success.error ? (
            <p className="text-sm text-red-600">Erreur de chargement.</p>
          ) : null}
        </StatCard>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <StatCard title="Commandes par statut" accent="brand">
          {byStatus.isLoading && loadingBlock}
          {byStatus.error && <p className="text-sm text-red-600">Erreur de chargement.</p>}
          {byStatus.data && (
            <div className="xl:col-span-2">
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(Object.entries(byStatus.data.data) as [OrderStatus, number][]).map(([status, count]) => ({
                      status,
                      label: ORDER_STATUS_LABELS[status],
                      count
                    }))}
                    margin={{ top: 8, right: 8, bottom: 8, left: 0 }}
                  >
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      angle={-22}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #e2e8f0' }}
                      cursor={{ fill: '#f1f5f9' }}
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {(Object.keys(byStatus.data.data) as OrderStatus[]).map((s) => (
                        <Cell key={s} fill={STATUS_COLORS[s]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(Object.entries(byStatus.data.data) as [OrderStatus, number][]).map(([status, count]) => (
                  <div
                    key={status}
                    className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs"
                  >
                    <StatusBadge status={status} />
                    <span className="font-semibold tabular-nums text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </StatCard>

        <StatCard title="Issue des livraisons" accent="emerald">
          {success.isLoading && loadingBlock}
          {success.error && <p className="text-sm text-red-600">Erreur de chargement.</p>}
          {success.data && (
            <div className="h-64 w-full">
              {(success.data.data.delivered + success.data.data.failed + success.data.data.cancelled) === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  Aucune commande terminée sur la période.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Livrées', value: success.data.data.delivered, key: 'delivered' },
                        { name: 'Échouées', value: success.data.data.failed, key: 'failed' },
                        { name: 'Annulées', value: success.data.data.cancelled, key: 'cancelled' }
                      ].filter((d) => d.value > 0)}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      <Cell fill={OUTCOME_COLORS.delivered} />
                      <Cell fill={OUTCOME_COLORS.failed} />
                      <Cell fill={OUTCOME_COLORS.cancelled} />
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #e2e8f0' }} />
                    <Legend
                      verticalAlign="bottom"
                      height={28}
                      wrapperStyle={{ fontSize: 12, color: '#475569' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </StatCard>
      </div>

      <StatCard title="Top livreurs" accent="slate">
        {top.isLoading && loadingBlock}
        {top.error && <p className="text-sm text-red-600">Erreur de chargement.</p>}
        {top.data && top.data.data.length === 0 && (
          <p className="text-sm text-slate-500">Aucune donnée sur la période.</p>
        )}
        {top.data && top.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="bg-slate-50/90 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Livreur</th>
                  <th className="px-4 py-3 font-medium text-right">Livrées</th>
                  <th className="px-4 py-3 font-medium text-right">Revenu</th>
                  <th className="px-4 py-3 font-medium text-right">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {top.data.data.map((d, idx) => (
                  <tr key={d.delivererId} className="transition-colors hover:bg-slate-50/80">
                    <td className="px-4 py-3 font-medium text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3 text-slate-800">
                      {d.firstName || d.lastName ? (
                        <span>{[d.firstName, d.lastName].filter(Boolean).join(' ')}</span>
                      ) : (
                        <span className="font-mono text-xs text-slate-500">{d.delivererId.slice(0, 8)}…</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{d.completedCount}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{d.totalRevenue.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {d.rating != null ? d.rating.toFixed(1) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </StatCard>
    </div>
  );
};
